import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export type AnalyticsPeriod = '7d' | '30d' | '90d' | '6m' | '1y' | 'all'
export type AnalyticsSegment = 'luxury' | 'mid' | 'entry'
export type UnitStatus = 'available' | 'reserved' | 'sold' | 'unknown'
export type ProjectStatus =
  | 'active'
  | 'sold_out'
  | 'pending'
  | 'cancelled'
  | 'completed'
  | 'inactive'

const UNIT_STATUSES: UnitStatus[] = ['available', 'reserved', 'sold', 'unknown']
const PROJECT_STATUSES: ProjectStatus[] = [
  'active',
  'sold_out',
  'pending',
  'cancelled',
  'completed',
  'inactive',
]

export interface AnalyticsFilters {
  period: AnalyticsPeriod
  cities: string[]
  districts: string[]
  typologies: string[]
  segment: AnalyticsSegment | null
  min_price: number | null
  max_price: number | null
  min_sqm: number | null
  max_sqm: number | null
  include_outliers: boolean
  // P2.1: entity filters
  developer_ids: number[]
  broker_ids: number[]
  architect_ids: number[]
  // P2.2: status + date ranges
  status: UnitStatus[]
  project_status: ProjectStatus[]
  listing_date_from: string | null
  listing_date_to: string | null
  sales_date_from: string | null
  sales_date_to: string | null
}

export const DEFAULT_FILTERS: AnalyticsFilters = {
  period: '90d',
  cities: [],
  districts: [],
  typologies: [],
  segment: null,
  min_price: null,
  max_price: null,
  min_sqm: null,
  max_sqm: null,
  include_outliers: false,
  developer_ids: [],
  broker_ids: [],
  architect_ids: [],
  status: [],
  project_status: [],
  listing_date_from: null,
  listing_date_to: null,
  sales_date_from: null,
  sales_date_to: null,
}

interface AnalyticsFiltersContextValue {
  filters: AnalyticsFilters
  setFilter: <K extends keyof AnalyticsFilters>(key: K, value: AnalyticsFilters[K]) => void
  setFilters: (next: Partial<AnalyticsFilters>) => void
  reset: () => void
  /** Query-string params object suitable for passing to api.get(..., { params }) */
  queryParams: Record<string, string | boolean>
}

const Ctx = createContext<AnalyticsFiltersContextValue | null>(null)

function parseCsv(value: string | null): string[] {
  if (!value) return []
  return value.split(',').map((s) => s.trim()).filter(Boolean)
}

function parseNumCsv(value: string | null): number[] {
  if (!value) return []
  return value
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n))
}

function parseStatusCsv(value: string | null): UnitStatus[] {
  if (!value) return []
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is UnitStatus => (UNIT_STATUSES as string[]).includes(s))
}

function parseProjectStatusCsv(value: string | null): ProjectStatus[] {
  if (!value) return []
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is ProjectStatus => (PROJECT_STATUSES as string[]).includes(s))
}

function parseNum(value: string | null): number | null {
  if (!value) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function parseDate(value: string | null): string | null {
  if (!value) return null
  // Accept only YYYY-MM-DD; anything else is discarded so we don't send
  // garbage to the backend @IsDateString validator.
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null
}

function parseSegment(value: string | null): AnalyticsSegment | null {
  if (value === 'luxury' || value === 'mid' || value === 'entry') return value
  return null
}

function parsePeriod(value: string | null): AnalyticsPeriod {
  if (value === '7d' || value === '30d' || value === '90d' || value === '6m' || value === '1y' || value === 'all') {
    return value
  }
  return DEFAULT_FILTERS.period
}

export function AnalyticsFiltersProvider({ children }: { children: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo<AnalyticsFilters>(
    () => ({
      period: parsePeriod(searchParams.get('period')),
      cities: parseCsv(searchParams.get('cities')),
      districts: parseCsv(searchParams.get('districts')),
      typologies: parseCsv(searchParams.get('typologies')),
      segment: parseSegment(searchParams.get('segment')),
      min_price: parseNum(searchParams.get('min_price')),
      max_price: parseNum(searchParams.get('max_price')),
      min_sqm: parseNum(searchParams.get('min_sqm')),
      max_sqm: parseNum(searchParams.get('max_sqm')),
      include_outliers: searchParams.get('include_outliers') === 'true',
      developer_ids: parseNumCsv(searchParams.get('developer_ids')),
      broker_ids: parseNumCsv(searchParams.get('broker_ids')),
      architect_ids: parseNumCsv(searchParams.get('architect_ids')),
      status: parseStatusCsv(searchParams.get('status')),
      project_status: parseProjectStatusCsv(searchParams.get('project_status')),
      listing_date_from: parseDate(searchParams.get('listing_date_from')),
      listing_date_to: parseDate(searchParams.get('listing_date_to')),
      sales_date_from: parseDate(searchParams.get('sales_date_from')),
      sales_date_to: parseDate(searchParams.get('sales_date_to')),
    }),
    [searchParams],
  )

  const writeToSearchParams = useCallback(
    (next: AnalyticsFilters) => {
      const params = new URLSearchParams()
      if (next.period !== DEFAULT_FILTERS.period) params.set('period', next.period)
      if (next.cities.length) params.set('cities', next.cities.join(','))
      if (next.districts.length) params.set('districts', next.districts.join(','))
      if (next.typologies.length) params.set('typologies', next.typologies.join(','))
      if (next.segment) params.set('segment', next.segment)
      if (next.min_price !== null) params.set('min_price', String(next.min_price))
      if (next.max_price !== null) params.set('max_price', String(next.max_price))
      if (next.min_sqm !== null) params.set('min_sqm', String(next.min_sqm))
      if (next.max_sqm !== null) params.set('max_sqm', String(next.max_sqm))
      if (next.include_outliers) params.set('include_outliers', 'true')
      if (next.developer_ids.length)
        params.set('developer_ids', next.developer_ids.join(','))
      if (next.broker_ids.length)
        params.set('broker_ids', next.broker_ids.join(','))
      if (next.architect_ids.length)
        params.set('architect_ids', next.architect_ids.join(','))
      if (next.status.length) params.set('status', next.status.join(','))
      if (next.project_status.length) params.set('project_status', next.project_status.join(','))
      if (next.listing_date_from) params.set('listing_date_from', next.listing_date_from)
      if (next.listing_date_to) params.set('listing_date_to', next.listing_date_to)
      if (next.sales_date_from) params.set('sales_date_from', next.sales_date_from)
      if (next.sales_date_to) params.set('sales_date_to', next.sales_date_to)
      setSearchParams(params, { replace: true })
    },
    [setSearchParams],
  )

  const setFilter = useCallback<AnalyticsFiltersContextValue['setFilter']>(
    (key, value) => {
      writeToSearchParams({ ...filters, [key]: value })
    },
    [filters, writeToSearchParams],
  )

  const setFilters = useCallback(
    (next: Partial<AnalyticsFilters>) => {
      writeToSearchParams({ ...filters, ...next })
    },
    [filters, writeToSearchParams],
  )

  const reset = useCallback(() => writeToSearchParams(DEFAULT_FILTERS), [writeToSearchParams])

  const queryParams = useMemo<Record<string, string | boolean>>(() => {
    const out: Record<string, string | boolean> = { period: filters.period }
    if (filters.cities.length) out.cities = filters.cities.join(',')
    if (filters.districts.length) out.districts = filters.districts.join(',')
    if (filters.typologies.length) out.typologies = filters.typologies.join(',')
    if (filters.segment) out.segment = filters.segment
    if (filters.min_price !== null) out.min_price = String(filters.min_price)
    if (filters.max_price !== null) out.max_price = String(filters.max_price)
    if (filters.min_sqm !== null) out.min_sqm = String(filters.min_sqm)
    if (filters.max_sqm !== null) out.max_sqm = String(filters.max_sqm)
    if (filters.include_outliers) out.include_outliers = true
    if (filters.developer_ids.length)
      out.developer_ids = filters.developer_ids.join(',')
    if (filters.broker_ids.length) out.broker_ids = filters.broker_ids.join(',')
    if (filters.architect_ids.length)
      out.architect_ids = filters.architect_ids.join(',')
    if (filters.status.length) out.status = filters.status.join(',')
    if (filters.project_status.length) out.project_status = filters.project_status.join(',')
    if (filters.listing_date_from) out.listing_date_from = filters.listing_date_from
    if (filters.listing_date_to) out.listing_date_to = filters.listing_date_to
    if (filters.sales_date_from) out.sales_date_from = filters.sales_date_from
    if (filters.sales_date_to) out.sales_date_to = filters.sales_date_to
    return out
  }, [filters])

  return (
    <Ctx.Provider value={{ filters, setFilter, setFilters, reset, queryParams }}>
      {children}
    </Ctx.Provider>
  )
}

export function useAnalyticsFilters() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAnalyticsFilters must be used inside <AnalyticsFiltersProvider>')
  return v
}
